(function () {
  class HeadboardVariantPicker extends HTMLElement {
    async connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      this.styleSelect = this.querySelector("[data-hvp-style-select]");
      this.sizeSelect = this.querySelector("[data-hvp-size-select]");
      this.colourInputs = Array.from(this.querySelectorAll("[data-hvp-colour-input]"));
      this.colourLabelValue = this.querySelector("[data-hvp-colour-value]");
      this.previewImage = this.querySelector("[data-hvp-preview-image]");
      this.previewFallback = this.querySelector("[data-hvp-preview-fallback]");
      this.variantIdInput = this.querySelector("[data-hvp-variant-id-input]");
      this.proxyRadio = this.querySelector("[data-hvp-proxy-radio]");

      this.styleIndex = Number(this.dataset.styleIndex ?? -1);
      this.colorIndex = Number(this.dataset.colorIndex ?? 0);
      this.sizeIndex = Number(this.dataset.sizeIndex ?? 1);
      this.hasStyleOption = this.styleIndex >= 0;
      this.productId = this.dataset.productId || "";
      this.variantsCount = Number(this.dataset.variantsCount || 0);
      this.embeddedVariantsCount = Number(this.dataset.embeddedVariantsCount || 0);
      this.storefrontAccessToken = this.resolveStorefrontToken();
      this.styleOptionName = (this.dataset.styleOptionName || "Style").trim();
      this.sizeOptionName = (this.dataset.sizeOptionName || "Size").trim();
      this.colorOptionName = (this.dataset.colorOptionName || "Color").trim();
      this.productHandle = this.resolveProductHandle();

      this.variants = this.readVariants();
      const catalogVariants = this.readVariantCatalog();
      if (catalogVariants.length) {
        this.variants = catalogVariants;
      }

      const shouldLoadStorefront =
        this.storefrontAccessToken &&
        (this.isCatalogIncomplete() || catalogVariants.length === 0);

      if (shouldLoadStorefront) {
        this.setAttribute("data-hvp-loading", "true");
        await this.loadAllVariants();
        this.removeAttribute("data-hvp-loading");
      } else if (this.isCatalogIncomplete()) {
      }

      this.bindEvents();
      await this.syncFromCurrentState({ emitEvents: false });
    }

    readVariants() {
      const script = this.querySelector("[data-hvp-variants]");
      if (!script) return [];
      try {
        const parsed = JSON.parse(script.textContent || "[]");
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }

    readVariantCatalog() {
      const script = this.querySelector("[data-hvp-variant-catalog]");
      if (!script) return [];
      try {
        const parsed = JSON.parse(script.textContent || "[]");
        const rows = Array.isArray(parsed) ? parsed : parsed.variants || [];
        return rows
          .map((row) => ({
            id: String(row.id || ""),
            available: row.available !== false,
            options: Array.isArray(row.options) ? row.options : []
          }))
          .filter((row) => row.id && row.options.length);
      } catch (error) {
        return [];
      }
    }

    resolveStorefrontToken() {
      const ownToken = (this.dataset.storefrontAccessToken || "").trim();
      if (ownToken) return ownToken;

      const donor = document.querySelector("[data-storefront-access-token]");
      if (!donor || donor === this) return "";

      return (donor.dataset.storefrontAccessToken || "").trim();
    }

    resolveProductHandle() {
      const fromDataset = (this.dataset.productHandle || "").trim();
      if (fromDataset) return fromDataset;

      const metaProduct = window.Shopify?.analytics?.meta?.product;
      if (metaProduct?.handle) return String(metaProduct.handle).trim();

      return "";
    }

    isCatalogIncomplete() {
      const embedded = this.variants.length;
      const expected = this.variantsCount || this.embeddedVariantsCount;
      return embedded >= 250 || (expected > 0 && embedded < expected);
    }

    getStorefrontApiUrl() {
      const shop =
        window.Shopify?.shop ||
        this.dataset.shopPermanentDomain ||
        window.location.hostname;
      if (!shop) return null;
      return `https://${shop}/api/2024-10/graphql.json`;
    }

    findOptionIndex(optionNames, candidates) {
      const normalized = optionNames.map((name) => String(name || "").trim().toLowerCase());
      for (const candidate of candidates) {
        const index = normalized.indexOf(String(candidate || "").trim().toLowerCase());
        if (index >= 0) return index;
      }
      return -1;
    }

    applyOptionIndicesFromProduct(product) {
      if (!product?.options || !Array.isArray(product.options)) return;

      const optionNames = product.options.map((option) => option.name);
      const styleIdx = this.findOptionIndex(optionNames, [
        this.styleOptionName,
        "Style"
      ]);
      const sizeIdx = this.findOptionIndex(optionNames, [this.sizeOptionName, "Size"]);
      const colorIdx = this.findOptionIndex(optionNames, [
        this.colorOptionName,
        "Color",
        "Colour"
      ]);

      if (sizeIdx >= 0) this.sizeIndex = sizeIdx;
      if (colorIdx >= 0) this.colorIndex = colorIdx;
      if (styleIdx >= 0) {
        this.styleIndex = styleIdx;
        this.hasStyleOption = true;
      }

      const variantCount = product.variantsCount?.count ?? product.variantsCount;
      if (variantCount) {
        this.variantsCount = Number(variantCount);
      }
    }

    parseStorefrontVariantGid(gid) {
      if (!gid) return "";
      const match = String(gid).match(/ProductVariant\/(\d+)/);
      return match ? match[1] : "";
    }

    mapStorefrontVariantNode(node) {
      if (!node || !Array.isArray(node.selectedOptions)) return null;

      const id = this.parseStorefrontVariantGid(node.id);
      if (!id) return null;

      const options = node.selectedOptions.map((opt) => String(opt.value || "").trim());
      const requiredLength = Math.max(this.styleIndex, this.colorIndex, this.sizeIndex) + 1;

      if (options.length < requiredLength) return null;
      if (!options[this.sizeIndex] || !options[this.colorIndex]) return null;
      if (this.styleIndex >= 0 && !options[this.styleIndex]) return null;

      return {
        id,
        available: node.availableForSale !== false,
        options
      };
    }

    async fetchStorefrontVariantPage(apiUrl, token, query, variables) {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token
        },
        body: JSON.stringify({ query, variables })
      });

      if (!response.ok) {
        return { product: null, error: `HTTP ${response.status}` };
      }

      const payload = await response.json();
      if (payload.errors && payload.errors.length) {
        const message = payload.errors.map((error) => error.message).join("; ");
        return { product: null, error: message };
      }

      return { product: payload.data?.product || null, error: null };
    }

    async fetchAllVariantsStorefront(handle) {
      const token = this.storefrontAccessToken;
      const apiUrl = this.getStorefrontApiUrl();
      if (!token || !apiUrl) return null;

      const productFields = `
        variantsCount { count }
        options { name }
        variants(first: 250, after: $cursor) {
          pageInfo { hasNextPage endCursor }
          edges {
            node {
              id
              availableForSale
              selectedOptions { name value }
            }
          }
        }
      `;

      const queryByHandle = `
        query HeadboardProductVariants($handle: String!, $cursor: String) {
          product(handle: $handle) {
            ${productFields}
          }
        }
      `;

      const queryById = `
        query HeadboardProductVariantsById($id: ID!, $cursor: String) {
          product(id: $id) {
            ${productFields}
          }
        }
      `;

      const productGid = this.productId ? `gid://shopify/Product/${this.productId}` : "";

      const fetchPages = async (query, baseVariables) => {
        let cursor = null;
        const allVariants = [];
        let rawCount = 0;
        let pageCount = 0;
        const maxPages = 12;
        let productFound = false;
        let expectedCount = 0;

        while (pageCount < maxPages) {
          const variables = { ...baseVariables, cursor };

          const { product, error } = await this.fetchStorefrontVariantPage(
            apiUrl,
            token,
            query,
            variables
          );

          if (!product) {
            if (!productFound && error) {
            }
            break;
          }

          productFound = true;
          if (pageCount === 0) {
            this.applyOptionIndicesFromProduct(product);
            expectedCount = Number(product.variantsCount?.count || 0);
          }

          const connection = product.variants;
          if (!connection) break;

          connection.edges.forEach((edge) => {
            rawCount += 1;
            const entry = this.mapStorefrontVariantNode(edge.node);
            if (entry) allVariants.push(entry);
          });

          if (!connection.pageInfo?.hasNextPage) break;
          cursor = connection.pageInfo.endCursor;
          pageCount += 1;
        }

        if (rawCount > allVariants.length) {
        }

        if (expectedCount > 0 && allVariants.length < expectedCount) {
        }

        return { allVariants, productFound };
      };

      try {
        let result = await fetchPages(queryByHandle, { handle });

        if (!result.productFound && productGid) {
          result = await fetchPages(queryById, { id: productGid });
        }

        if (!result.productFound) {
          return null;
        }

        return result.allVariants.length ? result.allVariants : null;
      } catch (error) {
        return null;
      }
    }

    async loadAllVariants() {
      const handle = this.productHandle;
      if (!handle) {
        return;
      }

      const embeddedCount = this.variants.length;
      const storefrontVariants = await this.fetchAllVariantsStorefront(handle);

      if (storefrontVariants?.length) {
        this.variants = storefrontVariants;

        if (this.variantsCount > 0 && storefrontVariants.length < this.variantsCount) {
        }
        return;
      }

      if (this.isCatalogIncomplete()) {
      }
    }

    bindEvents() {
      if (this.styleSelect) {
        this.styleSelect.addEventListener("change", () => {
          void this.syncFromCurrentState({ emitEvents: true });
        });
      }

      if (this.sizeSelect) {
        this.sizeSelect.addEventListener("change", () => {
          void this.syncFromCurrentState({ emitEvents: true });
        });
      }

      this.colourInputs.forEach((input) => {
        input.addEventListener("change", () => {
          void this.syncFromCurrentState({ emitEvents: true });
        });
      });
    }

    getSelectedColourInput() {
      return this.colourInputs.find((input) => input.checked) || null;
    }

    getSelectedStyle() {
      if (!this.hasStyleOption || !this.styleSelect) return null;
      return this.styleSelect.value;
    }

    variantMatchesOptions(variant, style, size, colour) {
      const options = variant.options || [];
      if (this.hasStyleOption && style && options[this.styleIndex] !== style) return false;
      if (size && options[this.sizeIndex] !== size) return false;
      if (colour && options[this.colorIndex] !== colour) return false;
      return true;
    }

    updateSizeOptionsForStyle(selectedStyle) {
      if (!this.sizeSelect) return;

      const style = this.hasStyleOption ? selectedStyle : null;
      let firstEnabledSize = null;
      let currentSizeStillValid = false;

      Array.from(this.sizeSelect.options).forEach((option) => {
        const sizeValue = option.value;
        const possible = this.variants.some((variant) => {
          if (!variant.available) return false;
          const options = variant.options || [];
          if (this.hasStyleOption && options[this.styleIndex] !== style) return false;
          return options[this.sizeIndex] === sizeValue;
        });

        option.disabled = !possible;
        if (possible && !firstEnabledSize) firstEnabledSize = sizeValue;
        if (possible && sizeValue === this.sizeSelect.value) currentSizeStillValid = true;
      });

      if (!currentSizeStillValid && firstEnabledSize) {
        this.sizeSelect.value = firstEnabledSize;
      }
    }

    async syncFromCurrentState({ emitEvents }) {
      const selectedStyle = this.getSelectedStyle();
      const selectedColourInput = this.getSelectedColourInput();
      const selectedColour = selectedColourInput ? selectedColourInput.value : "";

      if (this.hasStyleOption) {
        this.updateSizeOptionsForStyle(selectedStyle);
      }

      let selectedSize = this.sizeSelect ? this.sizeSelect.value : "";

      if (!selectedColour || !selectedSize) return;
      if (this.hasStyleOption && !selectedStyle) return;

      const exactVariant = this.findMatchingVariant(selectedStyle, selectedSize, selectedColour);
      let nextVariant = exactVariant;

      if (!nextVariant || nextVariant.available === false) {
        const fallback = this.findFirstAvailableVariant(selectedStyle, selectedColour);
        if (fallback) {
          nextVariant = fallback;
          selectedSize = (fallback.options || [])[this.sizeIndex] || selectedSize;
          if (this.sizeSelect && this.sizeSelect.value !== selectedSize) {
            this.sizeSelect.value = selectedSize;
          }
        }
      }

      this.updateUI(selectedColourInput, selectedColour, nextVariant);

      if (!nextVariant) return;

      this.updateVariantInputs(nextVariant.id);
      this.updateProxyVariantState(nextVariant);
      this.updateUrl(nextVariant.id);

      if (emitEvents) {
        await this.dispatchCompatibilityEvents(nextVariant);
      }
    }

    findMatchingVariant(selectedStyle, selectedSize, selectedColour) {
      const exactMatch = this.variants.find((variant) =>
        this.variantMatchesOptions(variant, selectedStyle, selectedSize, selectedColour)
      );
      if (exactMatch) return exactMatch;

      return (
        this.variants.find((variant) => {
          const options = variant.options || [];
          if (this.hasStyleOption && options[this.styleIndex] !== selectedStyle) return false;
          return options[this.colorIndex] === selectedColour;
        }) || null
      );
    }

    findFirstAvailableVariant(selectedStyle, selectedColour) {
      return (
        this.variants.find((variant) => {
          if (!variant.available) return false;
          const options = variant.options || [];
          if (this.hasStyleOption && options[this.styleIndex] !== selectedStyle) return false;
          return options[this.colorIndex] === selectedColour;
        }) || null
      );
    }

    updateUI(selectedColourInput, selectedColour, variant) {
      if (this.colourLabelValue) {
        this.colourLabelValue.textContent = selectedColour;
      }

      if (selectedColourInput) {
        const imageUrl = selectedColourInput.dataset.hvpSwatchImage || "";
        const hex = selectedColourInput.dataset.hvpHex || "#D9D9D9";

        if (this.previewImage) {
          if (imageUrl) {
            this.previewImage.src = imageUrl;
            this.previewImage.alt = selectedColour;
            this.previewImage.classList.remove("is-hidden");
          } else {
            this.previewImage.classList.add("is-hidden");
          }
        }

        if (this.previewFallback) {
          this.previewFallback.style.setProperty("--hvp-swatch-color", hex);
          if (imageUrl) {
            this.previewFallback.classList.add("is-hidden");
          } else {
            this.previewFallback.classList.remove("is-hidden");
          }
        }
      }

      const swatchChanged = this.updateUnavailableState(selectedColourInput);
      this.setSizeDisabledState(variant ? variant.available === false : true);

      if (swatchChanged) {
        void this.syncFromCurrentState({ emitEvents: false });
      }
    }

    updateUnavailableState(selectedColourInput) {
      const selectedStyle = this.getSelectedStyle();
      const sizeValue = this.sizeSelect ? this.sizeSelect.value : "";

      this.colourInputs.forEach((input) => {
        const possible = this.variants.some((variant) => {
          if (!variant.available) return false;
          return this.variantMatchesOptions(variant, selectedStyle, sizeValue, input.value);
        });
        input.disabled = !possible;
      });

      if (selectedColourInput && selectedColourInput.disabled) {
        const fallback = this.colourInputs.find((input) => !input.disabled);
        if (fallback && fallback !== selectedColourInput) {
          fallback.checked = true;
          if (this.colourLabelValue) this.colourLabelValue.textContent = fallback.value;
          return true;
        }
      }

      return false;
    }

    setSizeDisabledState(isUnavailable) {
      if (!this.sizeSelect) return;
      this.sizeSelect.dataset.variantUnavailable = isUnavailable ? "true" : "false";
    }

    updateVariantInputs(variantId) {
      if (this.variantIdInput) {
        this.variantIdInput.value = String(variantId);
      }

      const targetInputs = this.findMainVariantInputs();

      if (targetInputs.length) {
        targetInputs.forEach((input) => {
          if (!input) return;
          input.value = String(variantId);
          input.dispatchEvent(new Event("input", { bubbles: true }));
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      } else if (this.variantIdInput) {
        this.variantIdInput.dispatchEvent(new Event("input", { bubbles: true }));
        this.variantIdInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    findMainVariantInputs() {
      const inputs = [];
      const seen = new Set();

      const addInput = (candidate) => {
        if (!candidate || seen.has(candidate)) return;
        seen.add(candidate);
        inputs.push(candidate);
      };

      const isValidProductForm = (form) => {
        if (!form) return false;
        const id = typeof form.id === "string" ? form.id.toLowerCase() : "";
        if (id.includes("installment")) return false;
        return true;
      };

      const getVariantInput = (form) => {
        if (!isValidProductForm(form)) return null;
        return form.querySelector("select[name='id'], input[name='id']");
      };

      const nearestForm = this.closest("form[action^='/cart/add']");
      addInput(getVariantInput(nearestForm));

      const addToCartComponent = document.querySelector("add-to-cart-component");
      if (addToCartComponent) {
        addInput(getVariantInput(addToCartComponent.closest("form[action^='/cart/add']")));
      }

      const section = this.closest(".shopify-section");
      if (section) {
        section
          .querySelectorAll("form[action^='/cart/add']")
          .forEach((form) => addInput(getVariantInput(form)));
      }

      document
        .querySelectorAll("form[action^='/cart/add']")
        .forEach((form) => addInput(getVariantInput(form)));

      return inputs;
    }

    updateUrl(variantId) {
      if (!window.history || !window.location) return;
      const url = new URL(window.location.href);
      url.searchParams.set("variant", String(variantId));
      window.history.replaceState({}, "", url.toString());
    }

    updateProxyVariantState(variant) {
      if (!(this.proxyRadio instanceof HTMLInputElement) || !variant) return;

      this.proxyRadio.dataset.currentChecked = "false";
      this.proxyRadio.checked = false;
      this.proxyRadio.removeAttribute("checked");

      this.proxyRadio.checked = true;
      this.proxyRadio.setAttribute("checked", "checked");
      this.proxyRadio.dataset.variantId = String(variant.id);
      this.proxyRadio.dataset.optionAvailable = variant.available ? "true" : "false";
      this.proxyRadio.dataset.currentChecked = "true";

      this.proxyRadio.dispatchEvent(new Event("click", { bubbles: true }));
    }

    async dispatchCompatibilityEvents(variant) {
      const selectedDetail = {
        resource: {
          id: String(variant.id)
        }
      };

      this.dispatchEvent(
        new CustomEvent("variant:selected", {
          bubbles: true,
          detail: selectedDetail
        })
      );

      const updateHtml = await this.fetchVariantSectionHtml(variant.id);

      const updateDetail = {
        resource: {
          id: String(variant.id),
          available: Boolean(variant.available)
        },
        sourceId: String(variant.id),
        data: {
          html: updateHtml,
          productId: String(this.productId || ""),
          newProduct: null
        }
      };

      this.dispatchEvent(
        new CustomEvent("variant:update", {
          bubbles: true,
          detail: updateDetail
        })
      );

      this.dispatchEvent(
        new CustomEvent("variant:change", {
          bubbles: true,
          detail: {
            productId: this.productId,
            variantId: variant.id,
            available: variant.available,
            options: variant.options || []
          }
        })
      );

      document.dispatchEvent(
        new CustomEvent("variant:change", {
          detail: {
            productId: this.productId,
            variantId: variant.id,
            available: variant.available,
            options: variant.options || []
          }
        })
      );
    }

    async fetchVariantSectionHtml(variantId) {
      const sectionElement = this.closest(".shopify-section");
      const productFormComponent = this.closest("product-form-component");
      let sectionId =
        productFormComponent?.dataset?.sectionId ||
        sectionElement?.dataset?.sectionId ||
        sectionElement?.id ||
        "";

      if (sectionId.startsWith("shopify-section-")) {
        sectionId = sectionId.replace("shopify-section-", "");
      }

      if (!sectionId) return document;

      try {
        const url = new URL(window.location.href);
        url.search = "";
        url.searchParams.set("section_id", sectionId);
        url.searchParams.set("variant", String(variantId));

        const response = await fetch(url.toString(), {
          headers: { Accept: "text/html" }
        });
        if (!response.ok) return document;

        const htmlText = await response.text();
        return new DOMParser().parseFromString(htmlText, "text/html");
      } catch (error) {
        return document;
      }
    }
  }

  if (!customElements.get("headboard-variant-picker")) {
    customElements.define("headboard-variant-picker", HeadboardVariantPicker);
  }
})();
